/**
 * Use this file for JavaScript code that you want to run in the front-end
 * on posts/pages that contain this block.
 *
 * When this file is defined as the value of the `viewScript` property
 * in `block.json` it will be enqueued on the front end of the site.
 *
 * Example:
 *
 * ```js
 * {
 *   "viewScript": "file:./view.js"
 * }
 * ```
 *
 * If you're not making any changes to this file because your project doesn't need any
 * JavaScript running in the front-end, then you should delete this file and remove
 * the `viewScript` property from `block.json`.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-metadata/#view-script
 */

function ensureYoutubeAPIReady() {
	return new Promise((resolve, reject) => {
		if (window.YT && window.YT.Player) {
			return resolve();
		}

		let otherYoutubeReadyCb = null;
		if (typeof window.onYouTubeIframeAPIReady !== "undefined") {
			otherYoutubeReadyCb = window.onYouTubeIframeAPIReady;
		}

		window.onYouTubeIframeAPIReady = () => {
			otherYoutubeReadyCb && otherYoutubeReadyCb();
			resolve();
		};

		let script = document.createElement("script");
		script.id = "youtube-iframe-script";
		script.src = "https://www.youtube.com/iframe_api";
		let firstScriptTag = document.getElementsByTagName("script")[0];
		firstScriptTag.parentNode.insertBefore(script, firstScriptTag);
	});
}

function ensureVimeoAPIReady() {
	return new Promise((resolve, reject) => {
		if (window.Vimeo && window.Vimeo.Player) {
			return resolve();
		}

		let script = document.createElement("script");
		script.id = "vimeo-iframe-script";
		script.src = "https://player.vimeo.com/api/player.js";

		// Set up onload event handler
		script.onload = () => {
			// Check if the necessary objects are now available
			if (window.Vimeo && window.Vimeo.Player) {
				resolve();
			} else {
				reject(
					new Error(
						"Vimeo API script loaded, but Vimeo or Vimeo.Player is still not available",
					),
				);
			}
		};

		// Set up onerror event handler
		script.onerror = () => {
			reject(new Error("Error loading Vimeo API script"));
		};

		let firstScriptTag = document.getElementsByTagName("script")[0];
		firstScriptTag.parentNode.insertBefore(script, firstScriptTag);
	});
}

const removePosterAndOverlay = (video) => {
	const poster = video.querySelector(".media-and-content-video__poster");
	const overlay = video.querySelector(".media-and-content-video__overlay");

	if (poster) {
		poster.style.opacity = 0;
		poster.style.pointerEvents = "none";
	}

	if (overlay) {
		overlay.style.opacity = 0;
		overlay.style.pointerEvents = "none";
	}
};

const enterKeyPressPlay = (video, play) => {
	const playButton = video.querySelector(
		".media-and-content-video__overlay-icon",
	);
	if (playButton) {
		playButton.addEventListener("keydown", (e) => {
			if (e.key === "Enter") {
				removePosterAndOverlay(video);
				play();
			}
		});
	}
};

const handleIframeVideos = () => {
	const videoCollection = document.getElementsByClassName(
		"media-and-content-video__stacking-grid",
	);

	if (videoCollection) {
		const videoArray = Array.from(videoCollection);

		ensureYoutubeAPIReady().then(() => {
			for (let i = 0; i < videoArray.length; i++) {
				const videoItem = videoArray[i];
				// Check if the current videoItem has the class 'source-video-youtube'
				if (videoItem.dataset.videoType === "video-youtube") {
					// If the class is found, run the function and stop the loop
					videoArray.forEach((video, i) => {
						const videoOverlay = video.querySelector(
							".media-and-content-video__overlay",
						);
						const videoPoster = video.querySelector(
							".media-and-content-video__poster",
						);
						// events if youtube video is loaded
						const frame = video.getElementsByTagName("iframe");
						if (frame.length) {
							if (video.dataset.videoType === "video-youtube") {
								if (videoOverlay || videoPoster) {
									frame[0].style.pointerEvents = "none";
								}

								let playAfterReady = false;
								let isPlayerReady = false;
								const player = new YT.Player(frame[0], {
									events: {
										onReady: (e) => {
											console.log("ready");
											isPlayerReady = true;
											if (playAfterReady) {
												player.playVideo();
											}
										},
										onStateChange: (e) => {
											if (e.target.getPlayerState() === 1) {
												frame[0].style.visibility = "visible";
												frame[0].style.pointerEvents = "auto";
												removePosterAndOverlay(video);
											}
											if (
												video.dataset.loop === "true" &&
												e.target.getPlayerState() === 0
											) {
												player.playVideo();
											}
										},
									},
								});

								function handlePlay() {
									if (!isPlayerReady) {
										playAfterReady = true;
									} else {
										player.playVideo();
									}
								}
								enterKeyPressPlay(video, handlePlay);
								// run function for autoplay
								if (video.dataset.autoplay === "true") {
									handlePlay();
								}

								video.addEventListener("click", () => {
									handlePlay();
								});
							}
						}
					});
					break;
				}
			}
		});

		for (let i = 0; i < videoArray.length; i++) {
			const videoItem = videoArray[i];
			// Check if the current videoItem has the class 'source-video-vimeo'
			if (videoItem.dataset.videoType === "video-vimeo") {
				// If the class is found, run the function and stop the loop
				ensureVimeoAPIReady().then(() => {
					videoArray.forEach((video, i) => {
						// events if Vimeo video is loaded
						const frame = video.getElementsByTagName("iframe");

						if (frame.length) {
							if (video.dataset.videoType === "video-vimeo") {
								frame[0].onload = function () {
									ensureVimeoAPIReady().then(() => {
										const player = new Vimeo.Player(frame[0]);
										const play = () => {
											player.play();
										};

										const poster = video.querySelector(
											".media-and-content-video__poster",
										);
										const overlay = video.querySelector(
											".media-and-content-video__overlay",
										);

										if (poster) {
											poster.addEventListener("click", () => {
												play();
											});
										}

										if (overlay) {
											overlay.addEventListener("click", () => {
												play();
											});
										}

										enterKeyPressPlay(video, play);

										player.on("play", () => {
											removePosterAndOverlay(video);
										});
									});
								};
							}
						}
					});
				});
				break;
			}
		}
	}
};

const handleLocalVideos = () => {
	const fullWidthVideoBlocks = document.querySelectorAll(
		".wp-block-block-builder-montessori-media-and-text-montessori",
	);

	fullWidthVideoBlocks.forEach((block) => {
		const video = block.querySelector(".media-and-content__local-video");
		const playButton = block.querySelector(".media-and-content-video__overlay");
		const stackingGrid = block.querySelector(
			".media-and-content-video__stacking-grid",
		);

		if (video) {
			const play = () => {
				video.play();
			};

			const poster = block.querySelector(".media-and-content-video__poster");
			const overlay = block.querySelector(".media-and-content-video__overlay");

			if (poster) {
				if (stackingGrid.dataset.controls === "true") {
					video.controls = false;
				}

				poster.addEventListener("click", () => {
					if (stackingGrid.dataset.controls === "true") {
						video.controls = true;
					}
					play();
				});
			}

			if (overlay) {
				overlay.addEventListener("click", () => {
					if (stackingGrid.dataset.controls === "true") {
						video.controls = true;
					}
					play();
				});
			}

			if (playButton) {
				playButton.addEventListener("keydown", (e) => {
					if (e.key === "Enter") {
						if (stackingGrid.dataset.controls === "true") {
							video.controls = true;
						}
						removePosterAndOverlay(video);
						play();
					}
				});
			}

			video.addEventListener("playing", function () {
				removePosterAndOverlay(block);
			});
		}
	});
};

if (document.readyState !== "loading") {
	handleIframeVideos();
	handleLocalVideos();
} else {
	document.addEventListener("DOMContentLoaded", handleIframeVideos);
	document.addEventListener("DOMContentLoaded", handleLocalVideos);
}
